import React, { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Paper,
  Button,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  Skeleton,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  FilterList as FilterListIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Description as DatasheetIcon,
  ShoppingCart as CartIcon
} from '@mui/icons-material';
import { partsService } from '../services/partsService';
import { useCart } from '../context/CartContext';

const ITEMS_PER_PAGE = 20;
const VIEW_MODES = {
  GRID: 'grid',
  LIST: 'list'
};

const PartsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // State management
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [viewMode, setViewMode] = useState(VIEW_MODES.GRID);
  // distributor filter reserved for future multi-source support
  const [sortBy, setSortBy] = useState('relevance');
  const [filters, setFilters] = useState({
    inStockOnly: false,
    withDatasheet: false,
    category: '',
    manufacturer: '',
    minPrice: '',
    maxPrice: ''
  });

  const currentPage = parseInt(searchParams.get('page') || '1');

  // React Query for parts data
  const {
    data: partsData,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ['parts', searchQuery, currentPage, sortBy, filters],
    queryFn: async () => {
      const options = {
        limit: ITEMS_PER_PAGE,
        offset: (currentPage - 1) * ITEMS_PER_PAGE,
        sortBy: sortBy === 'price' ? 'price' : undefined,
        ...filters
      };

      if (!searchQuery.trim()) {
        // No search query — show all available parts
        const parts = await partsService.getFeaturedParts(ITEMS_PER_PAGE);
        return { parts, total: parts.length };
      }

      const parts = await partsService.searchParts(searchQuery, options);
      return { parts, total: parts.length };
    },
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Categories query
  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => partsService.getCategories(),
    staleTime: 30 * 60 * 1000, // 30 minutes
  });

  // URL state management
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (currentPage > 1) params.set('page', currentPage.toString());
    setSearchParams(params, { replace: true });
  }, [searchQuery, currentPage, setSearchParams]);

  // Handlers
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (query) {
        newParams.set('q', query);
      } else {
        newParams.delete('q');
      }
      newParams.delete('page');
      return newParams;
    });
  }, [setSearchParams]);

  const handlePageChange = (event, page) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (page > 1) {
        newParams.set('page', page.toString());
      } else {
        newParams.delete('page');
      }
      return newParams;
    });
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handlePartClick = (part) => {
    navigate(`/parts/${part.componentId || part.id}/${encodeURIComponent(part.partNumber)}`, {
      state: { part }
    });
  };

  const { addToCart } = useCart();

  const handleAddToCart = (part, e) => {
    e.stopPropagation();
    const moq = part.minimumOrderQuantity || part.minimum_order_quantity || 1;
    addToCart(part, moq);
  };

  // Modern card design
  const renderPartCard = (part) => (
    <Paper
      key={part.id}
      elevation={0}
      sx={{
        height: 300,
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        borderRadius: 4,
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
          borderColor: 'primary.light',
        },
      }}
      onClick={() => handlePartClick(part)}
    >
      {/* Header with gradient */}
      <Box
        sx={{
          height: 80,
          background: `linear-gradient(135deg, ${part.category === 'Semiconductors' ? '#e3f2fd' :
            part.category === 'Passives' ? '#f3e5f5' :
              part.category === 'Connectors' ? '#e8f5e8' :
                part.category === 'Sensors' ? '#fff3e0' : '#f5f5f5'} 0%, #fafafa 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        {part.thumbnailUrl ? (
          <img
            src={part.thumbnailUrl}
            alt={part.displayName}
            style={{
              maxWidth: '70%',
              maxHeight: '70%',
              objectFit: 'contain'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        ) : (
          <InventoryIcon sx={{ fontSize: 32, color: 'primary.main', opacity: 0.6 }} />
        )}

        {/* Status indicator */}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: part.inStock ? '#4caf50' : '#ff9800',
            boxShadow: '0 0 0 3px rgba(255,255,255,0.8)'
          }}
        />
      </Box>

      {/* Content */}
      <Box sx={{ flexGrow: 1, p: 2.5, display: 'flex', flexDirection: 'column' }}>
        {/* Part number and manufacturer */}
        <Typography
          variant="h6"
          sx={{
            fontSize: '1.1rem',
            fontWeight: 700,
            mb: 0.5,
            color: 'text.primary',
            lineHeight: 1.2
          }}
        >
          {part.displayName}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 1.5,
            fontSize: '0.9rem',
            fontWeight: 500
          }}
        >
          {part.manufacturer}
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 2,
            fontSize: '0.8rem',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.4,
            minHeight: '2.2em'
          }}
        >
          {part.shortDescription}
        </Typography>

        {/* Tags */}
        <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
          {part.category && (
            <Chip
              label={part.category}
              size="small"
              sx={{
                fontSize: '0.7rem',
                height: 22,
                bgcolor: 'primary.light',
                color: 'primary.contrastText'
              }}
            />
          )}
          {part.inStock && (
            <Chip
              label="In Stock"
              size="small"
              sx={{
                fontSize: '0.7rem',
                height: 22,
                bgcolor: 'success.light',
                color: 'success.contrastText'
              }}
            />
          )}
        </Box>

        {/* Price and actions */}
        <Box sx={{ mt: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                fontSize: '1.4rem'
              }}
            >
              {part.formattedPrice}
            </Typography>

            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {part.datasheet && (
                <Tooltip title="View Datasheet">
                  <IconButton
                    size="small"
                    sx={{
                      bgcolor: 'action.hover',
                      '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(part.datasheet, '_blank');
                    }}
                  >
                    <DatasheetIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: 'text.secondary',
              mb: 1.5,
              fontSize: '0.75rem'
            }}
          >
            {part.availabilityText}
          </Typography>

          <Button
            variant="contained"
            fullWidth
            startIcon={<CartIcon />}
            onClick={(e) => handleAddToCart(part, e)}
            sx={{
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              py: 1.2,
              fontSize: '0.9rem',
              boxShadow: 'none',
              '&:hover': {
                boxShadow: '0 4px 16px rgba(25, 118, 210, 0.3)'
              }
            }}
          >
            Add to Cart
          </Button>
        </Box>
      </Box>
    </Paper>
  );

  // List view (simpler, more compact)
  const renderPartList = (part) => (
    <Paper
      key={part.id}
      elevation={0}
      sx={{
        p: 3,
        mb: 2,
        cursor: 'pointer',
        borderRadius: 3,
        transition: 'all 0.2s ease-in-out',
        border: '1px solid',
        borderColor: 'divider',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          borderColor: 'primary.main',
          transform: 'translateY(-1px)'
        },
      }}
      onClick={() => handlePartClick(part)}
    >
      {/* Stock indicator */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: 0,
          width: 4,
          height: 40,
          bgcolor: part.inStock ? 'success.main' : 'warning.main',
          borderRadius: '0 2px 2px 0'
        }}
      />

      <Grid container spacing={3} alignItems="center">
        {/* Product Image */}
        <Grid item xs={12} sm={2}>
          <Box
            sx={{
              width: 80,
              height: 80,
              bgcolor: '#f8f9fa',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 2,
              border: '1px solid #e9ecef',
              position: 'relative'
            }}
          >
            {part.imageUrl ? (
              <img
                src={part.imageUrl}
                alt={part.displayName}
                style={{
                  maxWidth: '70%',
                  maxHeight: '70%',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <InventoryIcon sx={{ color: 'text.secondary', fontSize: 28 }} />
            )}
          </Box>
        </Grid>

        {/* Product Details */}
        <Grid item xs={12} sm={6}>
          <Box sx={{ pl: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                mb: 0.5,
                fontSize: '1.1rem',
                color: 'text.primary'
              }}
            >
              {part.partNumber}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mb: 1,
                fontWeight: 500
              }}
            >
              {part.manufacturer}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.9rem',
                lineHeight: 1.4,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {part.description || part.shortDescription}
            </Typography>

            {/* Tags */}
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
              {part.category && (
                <Chip
                  label={part.category}
                  size="small"
                  sx={{
                    fontSize: '0.75rem',
                    height: 24,
                    bgcolor: 'primary.light',
                    color: 'primary.main',
                    fontWeight: 500
                  }}
                />
              )}
              {part.inStock && (
                <Chip
                  label="In Stock"
                  size="small"
                  sx={{
                    fontSize: '0.75rem',
                    height: 24,
                    bgcolor: 'success.light',
                    color: 'success.main',
                    fontWeight: 500
                  }}
                />
              )}
              {part.datasheet && (
                <Chip
                  label="Datasheet"
                  size="small"
                  icon={<DatasheetIcon sx={{ fontSize: '16px !important' }} />}
                  sx={{
                    fontSize: '0.75rem',
                    height: 24,
                    bgcolor: 'info.light',
                    color: 'info.main',
                    fontWeight: 500
                  }}
                />
              )}
            </Box>
          </Box>
        </Grid>

        {/* Pricing */}
        <Grid item xs={12} sm={2}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography
              variant="h5"
              sx={{
                color: 'primary.main',
                fontWeight: 700,
                mb: 0.5
              }}
            >
              {part.formattedPrice || '$0.00'}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: part.availability > 0 ? 'success.main' : 'warning.main',
                display: 'block',
                fontWeight: 500,
                mb: 0.5
              }}
            >
              {part.availabilityText || '0 in stock'}
            </Typography>

            {part.leadTime && (
              <Typography variant="caption" color="text.secondary" display="block">
                Lead time: {part.leadTime}
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Actions */}
        <Grid item xs={12} sm={2}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              variant="contained"
              fullWidth
              size="small"
              startIcon={<CartIcon />}
              onClick={(e) => handleAddToCart(part, e)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)'
                }
              }}
            >
              Add to Cart
            </Button>

            {part.datasheet && (
              <Button
                variant="outlined"
                fullWidth
                size="small"
                startIcon={<DatasheetIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(part.datasheet, '_blank');
                }}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '0.75rem'
                }}
              >
                Datasheet
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );

  return (
    <Box sx={{
      bgcolor: '#fafafa',
      minHeight: '100vh',
      '& @keyframes spin': {
        '0%': { transform: 'rotate(0deg)' },
        '100%': { transform: 'rotate(360deg)' }
      }
    }}>
      {/* Results Summary Bar */}
      <Box sx={{ bgcolor: 'white', py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Typography variant="body1" color="text.secondary">
            {isLoading
              ? 'Loading parts...'
              : partsData
                ? searchQuery
                  ? `Found ${partsData.total} results for "${searchQuery}"`
                  : `Showing ${partsData.total} available parts`
                : 'Browse electronic components'
            }
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          {/* Filters Sidebar */}
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                <FilterListIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Filters
              </Typography>

              {/* Stock & Features */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Availability
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.inStockOnly}
                      onChange={(e) => handleFilterChange('inStockOnly', e.target.checked)}
                    />
                  }
                  label="In Stock Only"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.withDatasheet}
                      onChange={(e) => handleFilterChange('withDatasheet', e.target.checked)}
                    />
                  }
                  label="Has Datasheet"
                />
              </Box>

              {/* Categories */}
              {categories && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Categories
                  </Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel>Select Category</InputLabel>
                    <Select
                      value={filters.category}
                      label="Select Category"
                      onChange={(e) => handleFilterChange('category', e.target.value)}
                    >
                      <MenuItem value="">All Categories</MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.name} ({category.count.toLocaleString()})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {/* Price Range */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Price Range
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Min"
                    value={filters.minPrice}
                    onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    placeholder="Max"
                    value={filters.maxPrice}
                    onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                    sx={{ flex: 1 }}
                  />
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Results Area */}
          <Grid item xs={12} md={9}>
            {/* Results Controls */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="relevance">Relevance</MenuItem>
                  <MenuItem value="price">Price</MenuItem>
                  <MenuItem value="availability">Availability</MenuItem>
                  <MenuItem value="manufacturer">Manufacturer</MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton
                  onClick={() => setViewMode(VIEW_MODES.GRID)}
                  color={viewMode === VIEW_MODES.GRID ? 'primary' : 'default'}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                >
                  <ViewModuleIcon />
                </IconButton>
                <IconButton
                  onClick={() => setViewMode(VIEW_MODES.LIST)}
                  color={viewMode === VIEW_MODES.LIST ? 'primary' : 'default'}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                >
                  <ViewListIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Error State */}
            {isError && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
                {error?.message || 'Failed to load parts. Please try again.'}
                <Button onClick={() => refetch()} sx={{ ml: 2 }}>
                  Retry
                </Button>
              </Alert>
            )}

            {/* Loading State */}
            {isLoading && (
              <Grid container spacing={3}>
                {Array.from({ length: 8 }).map((_, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                    <Paper sx={{ p: 2, borderRadius: 3 }}>
                      <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2, mb: 2 }} />
                      <Skeleton variant="text" height={28} />
                      <Skeleton variant="text" height={20} />
                      <Skeleton variant="text" height={40} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                        <Skeleton variant="text" width={60} height={24} />
                        <Skeleton variant="text" width={80} height={20} />
                      </Box>
                      <Skeleton variant="rectangular" height={36} sx={{ mt: 1, borderRadius: 2 }} />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* No Results */}
            {partsData && partsData.parts.length === 0 && !isLoading && (
              <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 4 }}>
                <InventoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  {searchQuery ? 'No Parts Found' : 'No Parts Available'}
                </Typography>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  {searchQuery
                    ? `No results for "${searchQuery}". Try different search terms or filters.`
                    : 'No components are currently listed in the marketplace.'}
                </Typography>
                {searchQuery && (
                  <Button variant="outlined" onClick={() => handleSearch('')} sx={{ mt: 2, borderRadius: 3 }}>
                    Clear Search
                  </Button>
                )}
              </Paper>
            )}

            {/* Results Grid/List */}
            {partsData && partsData.parts.length > 0 && (
              <>
                {viewMode === VIEW_MODES.GRID ? (
                  <Grid container spacing={3}>
                    {partsData.parts.map((part) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={part.id}>
                        {renderPartCard(part)}
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box>
                    {partsData.parts.map(renderPartList)}
                  </Box>
                )}

                {/* Pagination */}
                {partsData.total > ITEMS_PER_PAGE && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                    <Pagination
                      count={Math.ceil(partsData.total / ITEMS_PER_PAGE)}
                      page={currentPage}
                      onChange={handlePageChange}
                      size="large"
                      showFirstButton
                      showLastButton
                      sx={{
                        '& .MuiPaginationItem-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default PartsPage;